/**
 * WhatsApp message-template body-variable parsing & substitution.
 *
 * WhatsApp templates carry placeholders in their BODY text — either positional
 * (`{{1}}`, `{{2}}`) or named (`{{customer_name}}`). A template uses one format
 * throughout; `parameter_format` is POSITIONAL or NAMED.
 *
 * Single source of truth for placeholder detection: the outbound template ops
 * `detectParameterFormat` delegates here, and the closed-window team-send path
 * uses both functions to (a) tell the picker which variables a template needs
 * and (b) render the substituted transcript text.
 */

/** Matches `{{1}}` / `{{customer_name}}` — word chars only (same alphabet as the legacy `detectParameterFormat`). */
const PLACEHOLDER_RE = /\{\{(\w+)\}\}/g;

export interface ParsedTemplateBody {
  parameter_format: 'POSITIONAL' | 'NAMED';
  /** Distinct variable keys in render order. POSITIONAL: numeric ascending; NAMED: first-seen order. */
  variables: string[];
}

/**
 * Parse a template BODY text into its parameter format and ordered variable
 * keys. A body with no placeholders is POSITIONAL with an empty list. Format is
 * decided by the first placeholder (all-digits ⇒ POSITIONAL) — identical to the
 * legacy `detectParameterFormat`.
 */
export function parseTemplateBodyVariables(bodyText: string | null | undefined): ParsedTemplateBody {
  if (!bodyText) return { parameter_format: 'POSITIONAL', variables: [] };

  const seen: string[] = [];
  for (const match of bodyText.matchAll(PLACEHOLDER_RE)) {
    if (!seen.includes(match[1])) seen.push(match[1]);
  }
  if (seen.length === 0) return { parameter_format: 'POSITIONAL', variables: [] };

  if (/^\d+$/.test(seen[0])) {
    return {
      parameter_format: 'POSITIONAL',
      variables: [...seen].sort((a, b) => Number(a) - Number(b)),
    };
  }
  return { parameter_format: 'NAMED', variables: seen };
}

/**
 * Substitute `{{key}}` placeholders in a template BODY with caller values.
 * A missing or empty value leaves the raw `{{key}}` token in place (so a
 * preview shows what is still unfilled). `values` may be a positional array
 * (index 0 → `{{1}}`) or a key→value record.
 */
export function substituteTemplateBody(
  bodyText: string | null | undefined,
  values: string[] | Record<string, string> | null | undefined,
): string {
  if (!bodyText) return '';
  if (!values) return bodyText;

  const lookup = (key: string): string | undefined => {
    if (Array.isArray(values)) {
      const idx = Number(key);
      return Number.isInteger(idx) && idx >= 1 ? values[idx - 1] : undefined;
    }
    return values[key];
  };

  return bodyText.replace(PLACEHOLDER_RE, (raw, key: string) => {
    const value = lookup(key);
    return value === undefined || value === '' ? raw : value;
  });
}
